export { CHAT_QUERY_KEYS } from './query-keys';

export interface ThreadIdParams {
  threadId: string;
}

export interface ChatPagination {
  offset: number;
  limit: number;
  hasMore: boolean;
}

// Identity

export const CHAT_IDENTITY_KINDS = ['individual', 'entityDesk', 'gammaDesk'] as const;
export type ChatIdentityKind = (typeof CHAT_IDENTITY_KINDS)[number];

export const PARTICIPANT_TYPES = ['User', 'EntityDesk', 'GammaDesk'] as const;
export type ParticipantType = (typeof PARTICIPANT_TYPES)[number];

export const RECIPIENT_KINDS = ['user', 'entity', 'gamma'] as const;
export type RecipientKind = (typeof RECIPIENT_KINDS)[number];

export const RECIPIENT_KIND_BY_PARTICIPANT_TYPE: Record<ParticipantType, RecipientKind> = {
  User: 'user',
  EntityDesk: 'entity',
  GammaDesk: 'gamma',
};

export const TOPIC_COLORS = ['brand', 'info', 'warning', 'error', 'accent', 'neutral'] as const;
export type TopicColor = (typeof TOPIC_COLORS)[number];

// Thread

export interface Thread {
  id: string;
  name: string;
  type: ThreadType;
  senderId: string;
  senderType: ParticipantType;
  senderEntityId?: string;
  isBroadcast: boolean;
  isReplyToBroadcast: boolean;
  parentThreadId?: string;
  isArchived: boolean;
  isResolved: boolean;
  isMuted: boolean;
  isPrivate: boolean;
  isWaiting: boolean;
  /** Which held identity the thread was read through; only `GET`/`PATCH /threads/:threadId` set it. */
  ownIdentity?: ChatIdentityKind;
  participants: Participant[];
  totalParticipantsCount: number;
  messages: ChatMessage[];
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  unreadMessageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface GetThreadsParams {
  /**
   * Number of threads to return
   * @default 20
   */
  limit?: number;
  /**
   * Number of threads to skip
   * @default 0
   */
  offset?: number;
  /**
   * View threads grouped by thread or by recipient
   * - `thread`: Group messages by conversation thread
   * - `recipient`: Group messages by recipient contact
   * @default "recipient"
   */
  viewBy?: 'thread' | 'recipient';
  /**
   * Search term to filter threads
   */
  search?: string;
  /**
   * Filter by thread status
   */
  status?: ThreadsStatusParam[];
  /**
   * Sort threads by
   */
  sortBy?: 'recent' | 'oldest' | 'alphabetical';
  /**
   * Sort order
   */
  sortOrder?: GetThreadsSortOrderParam;
}

type ThreadsStatusParam = 'Read' | 'Unread' | 'Archived' | 'Unarchived' | 'Replied' | 'Unreplied';

export type GetThreadsSortOrderParam = 'asc' | 'desc';

export interface GetThreadsRes {
  threads: Thread[];
  total: number;
  pagination: ChatPagination;
}

export type GetThreadsByRecipientParams = Pick<
  GetThreadsParams,
  'search' | 'sortBy' | 'sortOrder' | 'status'
>;

export interface ThreadsByRecipient {
  recipientId: string;
  recipientType: ParticipantType;
  recipientName: string | null;
  recipientAlias: string | null;
  recipientEmail: string;
  recipientImageUrl: string | null;
  lastMessageAt: string;
  threads: Thread[];
  unreadCount: number;
}

export interface GetThreadsByRecipientRes {
  recipients: ThreadsByRecipient[];
  total: number;
  pagination: ChatPagination;
}

export type ThreadType = 'Broadcast' | 'Report' | 'Feedback' | 'Inquiry' | 'Support' | 'General';

/**
 * `Broadcast` is a persisted type the system gives a subject thread; no request may ask for one
 * (GP-4067).
 */
export type CreateThreadType = Exclude<ThreadType, 'Broadcast'>;

export interface ThreadAbout {
  type: 'Fund' | 'Company' | 'Event';
  id: string;
}

interface CreateThreadFields {
  name?: string;
  recipients?: ThreadRecipient[];
  initialMessage?: string;
  attachmentIds?: string[];
  forwardOfMessageId?: string;
}

export type CreateThreadBody = CreateThreadFields &
  (
    | {
        type: 'Inquiry';
        /** 400 `CHAT.THREAD.INVALID` when the id does not resolve. */
        about?: ThreadAbout;
      }
    | {
        /**
         * @default "General"
         */
        type?: Exclude<CreateThreadType, 'Inquiry'>;
        about?: never;
      }
  );

//  Participant

export interface Participant {
  id: string;
  participantId: string;
  participantType: ParticipantType;
  participantName: string | null;
  participantAlias: string | null;
  participantEmail: string;
  participantImageUrl: string | null;
  isRead: boolean;
  isArchived: boolean;
  isReplied: boolean;
  lastReadAt: string | null;
  archivedAt: string | null;
}

// Recipient

export interface ThreadRecipient {
  id: string;
  type: RecipientKind;
}

export interface Recipient {
  id: string;
  type: RecipientKind;
  name: string | null;
  /**
   * Secondary line under the name. Email-only for a person — the directory drops any value
   * without an `@`, so a "Role · Company" line never arrives.
   */
  subtitle: string | null;
  email: string;
  avatarUrl: string | null;
  /**
   * Why this recipient is reachable, when the reason is an Address Book connection — the "Direct
   * connection" chip renders off this. `null` for anyone surfaced for another reason.
   */
  connectionType: string | null;
}

export interface GetRecipientsRes {
  recipients: Recipient[];
  pagination: ChatPagination;
}

export interface GetRecipientsParams {
  /**
   * Number of recipients to return (max 100)
   * @default 20
   */
  limit?: number;
  /**
   * Number of recipients to skip (pagination offset)
   */
  offset?: number;
  /**
   * Search recipients by name or email (case-insensitive).
   */
  search?: string;
  /**
   * Narrow the directory to one participant id. Answers with an empty page when the acting
   * workspace cannot reach it.
   */
  recipientId?: string;
}

// Messages

export interface GetChatMessagesParams extends ThreadIdParams {
  /**
   * Number of messages to return
   * @default 50
   */
  limit?: number;
  /**
   * Number of messages to skip
   * @default 0
   */
  offset?: number;
}

export interface MessageReference {
  messageId: string;
  author: string | null;
  excerpt: string | null;
  /** Present only when the referenced message has at least one attachment. */
  attachmentCount?: number;
  /** Filename of the referenced message's first attachment. */
  attachmentFilename?: string;
  /** Only ever `true`; absent while the referenced message is alive. */
  deleted?: true;
}

export interface ChatMessage {
  id: string;
  content?: string;
  senderId: string;
  /** The human author behind the send, which differs from `senderId` for a shared inbox. */
  originalSenderId: string;
  senderType: ParticipantType;
  senderEntityId?: string;
  senderName: string | null;
  senderEmail: string;
  senderImageUrl: string | null;
  threadId: string;
  attachments: ChatMessageAttachment[];
  sentAt: string;
  isEdited: boolean;
  editedAt: string | null;
  additional?: { originalSenderId: string };
  replyTo?: MessageReference;
  forwardedFrom?: MessageReference;
  link?: string | null;
  isBroadcast?: boolean;
}

export interface SendMessageBody {
  content?: string;
  attachmentIds?: string[];
  /** Quote a message of this same thread; 422 `CHAT.MESSAGE.REPLY_TO_DIFFERENT_THREAD` otherwise. */
  replyToMessageId?: string;
  /** Mutually exclusive with `replyToMessageId`; 400 `CHAT.MESSAGE.REFERENCE_EXCLUSIVE` otherwise. */
  forwardOfMessageId?: string;
}

export interface SendMessageVars extends ThreadIdParams, SendMessageBody {}

export interface EditMessageVars {
  messageId: string;
  content: string;
}

const AttachmentStatus = {
  Ready: 'Ready',
  Failed: 'Failed',
  Pending: 'Pending',
} as const;

export type AttachmentStatus = (typeof AttachmentStatus)[keyof typeof AttachmentStatus];

export interface ChatMessageAttachment {
  fileId: string;
  filename: string;
  url: string;
  size: number;
  status: AttachmentStatus;
  contentType: string;
  urlExpiresAt: string;
}

export interface ReplyThreadBody {
  threadId: string;
  /** The entry being answered; it is forwarded into the responder's one thread with the owner. */
  messageId: string;
  initialMessage: string;
}

export interface ReplyThreadResponse {
  threadId: string;
  /** The subject-owner contact context that lists the private reply thread. */
  contextId: string;
  created: boolean;
}

export interface GetReplyThreadParams extends ThreadIdParams {
  messageId?: string;
}

export interface RequestFileUploadBody {
  threadId: string;
  filename: string;
  contentType?: string;
  size?: number;
}

export interface RequestFileUploadResponse {
  fileId: string;
  uploadUrl: string;
  /** Headers the PUT to `uploadUrl` must carry, or S3 answers `SignatureDoesNotMatch`. */
  uploadHeaders: Record<string, string>;
  expiresAt: string;
}

// Attachments availability

export interface CheckAttachmentsAvailabilityBody {
  threadId: string;
  fileIds: string[];
}

export interface AttachmentAvailability {
  fileId: string;
  available: boolean;
}

export interface CheckAttachmentsAvailabilityResponse {
  attachments: AttachmentAvailability[];
}
